import React from 'react';
import { X, ChevronDown, ChevronRight, Award, Clock, TrendingDown, Users, AlertTriangle } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet } from '../../hooks/useApi.js';
import type { Technician } from '../../api/types.js';

/**
 * Ranked technician picker (#99). When cwTicketId is provided, fetches the
 * dispatch engine's ranked candidates for THIS ticket and shows them with
 * score breakdown. Falls back to a plain list when no recommendation exists.
 */
interface RankedCandidate {
  techId: string;
  techName: string;
  score: number;
  skillScore: number;
  availabilityScore: number;
  workloadScore: number;
  familiarityScore: number;
  urgencyScore: number;
  reason: string;
  availableAt?: string;
  rank: number;
  proposedStart?: string;
  proposedEnd?: string;
  proposedReason?: string;
  unavailableReason?: string;
}

interface RecommendResponse {
  ticketCacheId: string;
  candidates: RankedCandidate[];
  mode: string;
  confidence: number | null;
  scheduleDataAvailable: boolean;
}

function scoreBadge(score: number): { label: string; color: string } {
  if (score >= 0.8) return { label: 'High', color: 'text-green-600 dark:text-green-400' };
  if (score >= 0.6) return { label: 'Medium', color: 'text-yellow-600 dark:text-yellow-400' };
  return { label: 'Low', color: 'text-red-600 dark:text-red-400' };
}

function ScoreBar({ label, value, icon: Icon }: { label: string; value: number; icon: React.ElementType }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="h-3 w-3 text-[hsl(var(--content-subtle))]" />
      <span className="w-24 text-[hsl(var(--content-subtle))]">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--surface-subtle))] overflow-hidden">
        <div className="h-full rounded-full bg-brand-500" style={{ width: pct + '%' }} />
      </div>
      <span className="w-8 text-right tabular-nums text-[hsl(var(--content))]">{pct}%</span>
    </div>
  );
}

function CandidateRow({ c, tech, selected, onSelect }: {
  c: RankedCandidate;
  tech: Technician | undefined;
  selected: boolean;
  onSelect: () => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const badge = scoreBadge(c.score);
  const unlinked = tech?.cwMemberId == null;
  const disabled = unlinked || !!c.unavailableReason;
  return (
    <div className={cn('rounded-lg border transition-colors', selected ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/30' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--border-strong))]', disabled && 'opacity-60')}>
      <button type="button" onClick={() => !disabled && onSelect()} disabled={disabled} className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--surface-subtle))] flex items-center justify-center text-xs font-bold text-[hsl(var(--content-subtle))]">{c.rank}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[hsl(var(--content))] truncate">{c.techName}</span>
            <span className={cn('text-xs font-semibold', badge.color)}>{badge.label}</span>
            {c.unavailableReason && <span className="text-xs text-red-500 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />{c.unavailableReason}</span>}
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] truncate">{c.reason}</p>
        </div>
        <span className="flex-shrink-0 text-sm font-bold tabular-nums text-[hsl(var(--content))]">{Math.round(c.score * 100)}%</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="flex-shrink-0 p-1 text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-1.5 border-t border-[hsl(var(--border))] pt-2">
          <ScoreBar label="Skill fit" value={c.skillScore} icon={Award} />
          <ScoreBar label="Availability" value={c.availabilityScore} icon={Clock} />
          <ScoreBar label="Workload" value={c.workloadScore} icon={TrendingDown} />
          <ScoreBar label="Familiarity" value={c.familiarityScore} icon={Users} />
          <ScoreBar label="Urgency fit" value={c.urgencyScore} icon={AlertTriangle} />
          {c.proposedStart && (
            <p className="text-xs text-[hsl(var(--content-subtle))] pt-1">
              Proposed: {new Date(c.proposedStart).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              {c.proposedReason ? ' — ' + c.proposedReason : ''}
            </p>
          )}
          {unlinked && <p className="text-xs text-red-500">Not linked to ConnectWise — cannot own a CW ticket.</p>}
        </div>
      )}
    </div>
  );
}

export function AssignTechDialog({
  open, title, description, confirmLabel = 'Assign', requireReason = false, requireCwMember = true, cwTicketId, onClose, onConfirm,
}: {
  open: boolean; title: string; description?: string; confirmLabel?: string; requireReason?: boolean; requireCwMember?: boolean; cwTicketId?: number; onClose: () => void; onConfirm: (techId: string, reason: string) => Promise<void>;
}) {
  const recommend = useGet<RecommendResponse>(open && cwTicketId ? '/api/v1/dispatch/recommend/' + cwTicketId : null);
  const fallback = useGet<{ items: Technician[] }>(open && !cwTicketId ? '/api/v1/technicians' : null);
  const [techId, setTechId] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => { if (open) { setTechId(''); setReason(''); setErr(null); } }, [open]);
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;
  const candidates = recommend.data?.candidates ?? [];
  const techs = fallback.data?.items ?? [];
  const loading = recommend.loading || fallback.loading;
  const error = recommend.error || fallback.error;
  const usingRanked = candidates.length > 0;
  const canSubmit = !!techId && (!requireReason || reason.trim().length > 0) && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try { await onConfirm(techId, reason.trim()); }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : String(e2)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="assign-dialog-title">
      <form onSubmit={submit} className="w-full max-w-lg rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
          <h2 id="assign-dialog-title" className="text-sm font-semibold text-[hsl(var(--content))]">{title}</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
          {description && <p className="text-xs text-[hsl(var(--content-subtle))]">{description}</p>}
          {loading ? (
            <p className="text-sm text-[hsl(var(--content-subtle))]">Loading recommendations…</p>
          ) : error ? (
            <p className="text-sm text-red-600">Could not load ({error}). <button type="button" className="underline" onClick={() => { recommend.refetch(); fallback.refetch(); }}>Retry</button></p>
          ) : usingRanked ? (
            <>
              {!recommend.data?.scheduleDataAvailable && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Schedule data unavailable — availability scores are estimates.</p>
              )}
              <div className="space-y-2">
                {candidates.map(c => (
                  <CandidateRow key={c.techId} c={c} tech={techs.find(t => t.id === c.techId)} selected={techId === c.techId} onSelect={() => setTechId(c.techId)} />
                ))}
              </div>
            </>
          ) : techs.length === 0 ? (
            <p className="text-sm text-[hsl(var(--content-subtle))]">No technicians yet. They appear after the ConnectWise member sync.</p>
          ) : (
            <select id="assign-tech" value={techId} onChange={e => setTechId(e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500">
              <option value="">Select a technician…</option>
              {techs.map(t => { const unlinked = requireCwMember && t.cwMemberId == null; return <option key={t.id} value={t.id} disabled={unlinked}>{t.name}{t.role ? ' · ' + t.role : ''} · {t.openTickets} open{unlinked ? ' (not linked to ConnectWise)' : ''}</option>; })}
            </select>
          )}
          {requireReason && (
            <>
              <label htmlFor="assign-reason" className="block text-xs font-medium text-[hsl(var(--content))]">Reason</label>
              <textarea id="assign-reason" value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Why this technician instead of the recommendation?" className="w-full px-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
            </>
          )}
          {err && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[hsl(var(--border))]">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]">Cancel</button>
          <button type="submit" disabled={!canSubmit} className={cn('rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed')}>{busy ? 'Working…' : confirmLabel}</button>
        </div>
      </form>
    </div>
  );
}
